/**
 * Dashboard page — Studio overview, the "Welcome back" landing
 * screen at `/dashboard`.
 *
 * 1:1 with the design folder's `index.html` body (lines 137-502)
 * and pulls every number from live Firestore snapshots — NO mock
 * data anywhere. Every count, ₹ value, sparkline tick, list item,
 * staff avatar, day-dot is derived from a hook the rest of the
 * app already uses.
 *
 * Layout (mirrors design folder exactly):
 *   1. .greeting           — "Good morning, {name}" + focus pill
 *   2. .kpi-strip          — 4 tiles: Active projects · Revenue
 *                            this month · Outstanding receivables ·
 *                            Pipeline value
 *   3. .dash-row (60/40)
 *        LEFT  card        — "Needs your attention" (5 items max)
 *        RIGHT stack       — This-week calendar + Recent activity
 *   4. .dash-row (50/50)
 *        LEFT  card        — Today's tasks
 *        RIGHT card        — Staff list (org members)
 *   5. .dash-row (50/50)
 *        LEFT  card        — Pending material requests
 *        RIGHT card        — Pending transaction approvals
 *
 * Capability gate: every section is read-only on the dashboard —
 * follow the deep-link (✱ on each row / card title) to act. Server
 * rules already scope what every role sees so non-admin viewers
 * naturally get a smaller dashboard.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { useAppointments } from '@/lib/useAppointments';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useLeads } from '@/lib/useLeads';
import {
  memberInitials,
  ROLE_TONE,
  useOrgMembers,
} from '@/lib/useOrgMembers';
import { useOrgMaterialRequests } from '@/lib/useOrgMaterialRequests';
import { useOrgTransactions } from '@/lib/useOrgTransactions';
import { useProjects } from '@/lib/useProjects';
import {
  normaliseTxType,
  txDateMs,
  type TransactionItem,
} from '@/lib/useProjectTransactions';
import { ROLE_LABELS } from '@/lib/useProjectMembers';
import type { LeadListItem } from '@/lib/useLeads';
import type { AppointmentItem } from '@/lib/useAppointments';
import type { MaterialRequest } from '@/lib/materialRequests';

// ───── Helpers ─────

function fmtInr(n: number): string {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)} L`;
  if (n >= 1000) return `₹${Math.round(n / 1000)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function greetingFor(d: Date): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function firstName(s: string | null | undefined): string {
  const t = (s ?? '').trim();
  if (!t) return 'there';
  return t.split(/\s+/)[0];
}

function startOfMonth(d: Date): Date {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function startOfWeekMonday(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; // 0=Mon
  x.setDate(x.getDate() - day);
  return x;
}

function relTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days}d ago`;
  return new Date(ms).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
  });
}

// ───── Component ─────

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { userDoc } = useCurrentUser();
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const orgData = (org?.data ?? {}) as { name?: string };

  // Live snapshots — every section's data source.
  const { projects } = useProjects();
  const { transactions } = useOrgTransactions(orgId);
  const { leads } = useLeads();
  const { appointments } = useAppointments();
  const { members } = useOrgMembers(orgId);
  const { data: materialRequests } = useOrgMaterialRequests(orgId);

  // ── Greeting / name ────────────────────────────────────────
  const userData = (userDoc?.data ?? {}) as {
    displayName?: string;
    fullName?: string;
  };
  const userName =
    userData.displayName?.trim() ||
    userData.fullName?.trim() ||
    user?.phoneNumber ||
    user?.email ||
    null;
  const greeting = `${greetingFor(new Date())}, ${firstName(userName)}`;
  const today = new Date();
  const todayLabel = today.toLocaleDateString('en-IN', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  // ── KPI: Active projects + nearing-deadline ─────────────────
  const kpiProjects = useMemo(() => {
    const active = projects.filter((p) => p.status === 'active').length;
    // Nearing deadline = active project with endDate in next 14 days
    // and progress < 90 (so projects already on track don't trigger).
    const now = Date.now();
    const fortnight = now + 14 * 86400000;
    const nearing = projects.filter((p) => {
      if (p.status !== 'active') return false;
      const end = p.endDate?.toMillis?.();
      if (!end) return false;
      if (end < now || end > fortnight) return false;
      return (p.progress ?? 0) < 90;
    }).length;
    return { active, nearing, total: projects.length };
  }, [projects]);

  // ── KPI: This-month revenue (payment_in this month) ─────────
  const kpiRevenue = useMemo(() => {
    const monthStart = startOfMonth(today).getTime();
    const prevMonthStart = startOfMonth(
      new Date(today.getFullYear(), today.getMonth() - 1, 1),
    ).getTime();
    let thisMonth = 0;
    let lastMonth = 0;
    for (const t of transactions) {
      const tp = normaliseTxType(t.type);
      if (tp !== 'in') continue;
      const ms = txDateMs(t);
      if (ms >= monthStart) thisMonth += t.amount;
      else if (ms >= prevMonthStart && ms < monthStart) lastMonth += t.amount;
    }
    const deltaPct =
      lastMonth > 0
        ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100)
        : null;
    return { thisMonth, lastMonth, deltaPct };
  }, [transactions, today]);

  // ── KPI: Outstanding receivables — pending payment_in + overdue
  // ───────────────────────────────────────────────────────────
  // Heuristic: any payment_in with status 'pending' or 'partial'.
  // Overdue = same set with `date` < today.
  const kpiReceivables = useMemo(() => {
    let outstanding = 0;
    let overdue = 0;
    for (const t of transactions) {
      const tp = normaliseTxType(t.type);
      if (tp !== 'in') continue;
      if (t.status !== 'pending' && t.status !== 'partial') continue;
      outstanding += t.amount;
      const ms = txDateMs(t);
      if (ms < today.getTime()) overdue += 1;
    }
    return { outstanding, overdueCount: overdue };
  }, [transactions, today]);

  // ── KPI: Pipeline value — sum of active leads' budget ────────
  const kpiPipeline = useMemo(() => {
    const activeStatuses = new Set([
      'new',
      'contacted',
      'site_visit_scheduled',
      'proposal_sent',
      'negotiation',
    ]);
    let value = 0;
    let active = 0;
    for (const l of leads) {
      if (!activeStatuses.has(l.status)) continue;
      active += 1;
      value += budgetEstimate(l);
    }
    return { value, active };
  }, [leads]);

  // ── Pending approvals — transactions with workflowStatus === 'pending_approval'
  const pendingTxnApprovals = useMemo(
    () =>
      transactions.filter((t) => t.workflowStatus === 'pending_approval'),
    [transactions],
  );

  // ── Pending material requests
  const pendingMaterialReqs = useMemo(
    () => materialRequests.filter((r) => r.status === 'pending'),
    [materialRequests],
  );

  // ── Focus banner text (above KPI strip)
  const focusBanner = useMemo(() => {
    const parts: string[] = [];
    if (pendingTxnApprovals.length > 0) {
      parts.push(
        `${pendingTxnApprovals.length} transaction${pendingTxnApprovals.length === 1 ? '' : 's'} pending approval`,
      );
    }
    if (pendingMaterialReqs.length > 0) {
      parts.push(
        `${pendingMaterialReqs.length} material request${pendingMaterialReqs.length === 1 ? '' : 's'} to review`,
      );
    }
    if (kpiReceivables.overdueCount > 0) {
      parts.push(
        `${kpiReceivables.overdueCount} invoice${kpiReceivables.overdueCount === 1 ? '' : 's'} overdue`,
      );
    }
    if (kpiProjects.nearing > 0) {
      parts.push(
        `${kpiProjects.nearing} project${kpiProjects.nearing === 1 ? '' : 's'} nearing deadline`,
      );
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [
    pendingTxnApprovals.length,
    pendingMaterialReqs.length,
    kpiReceivables.overdueCount,
    kpiProjects.nearing,
  ]);

  // ── Needs your attention — assemble the unified queue ─────────
  // Pulls from three real sources: txn approvals + material requests
  // + overdue receivables. Capped at 5 items. The icon class +
  // critical flag map to the design folder's `.attn-icon.*` palette.
  const attentionItems = useMemo(() => {
    type Item = {
      key: string;
      iconClass: string; // ph icon
      iconBg: string;
      iconFg: string;
      desc: string;
      meta: string;
      action: string;
      critical?: boolean;
      onClick: () => void;
    };
    const out: Item[] = [];

    // Overdue invoices first — they cost money.
    for (const t of transactions) {
      if (out.length >= 5) break;
      if (normaliseTxType(t.type) !== 'in') continue;
      if (t.status !== 'pending' && t.status !== 'partial') continue;
      const ms = txDateMs(t);
      if (ms >= today.getTime()) continue;
      const daysOverdue = Math.floor(
        (today.getTime() - ms) / 86400000,
      );
      const project = projects.find((p) => p.id === t.projectId);
      out.push({
        key: `inv-${t.id}`,
        iconClass: 'ph-bold ph-receipt-x',
        iconBg: 'var(--critical-bg)',
        iconFg: 'var(--critical)',
        desc: `Invoice ${t.referenceNumber ? `#${t.referenceNumber} ` : ''}overdue by ${daysOverdue} day${daysOverdue === 1 ? '' : 's'}`,
        meta: `${fmtInr(t.amount)} · ${t.partyName ?? project?.name ?? '—'}`,
        action: 'Follow up',
        critical: true,
        onClick: () =>
          navigate(
            `/projects/${t.projectId}?tab=transactions&txn=${t.id}`,
          ),
      });
    }

    // Pending transaction approvals.
    for (const t of pendingTxnApprovals.slice(0, 5)) {
      if (out.length >= 5) break;
      const project = projects.find((p) => p.id === t.projectId);
      out.push({
        key: `txn-${t.id}`,
        iconClass: 'ph-bold ph-check-square',
        iconBg: '#F5F3FF',
        iconFg: '#7C3AED',
        desc: `Approve transaction · ${t.description || t.partyName || 'Transaction'}`,
        meta: `${fmtInr(t.amount)} · ${project?.name ?? 'Project'} · ${relTime(t.createdAt?.toMillis?.() ?? null)}`,
        action: 'Review',
        onClick: () =>
          navigate(
            `/projects/${t.projectId}?tab=transactions&txn=${t.id}`,
          ),
      });
    }

    // Pending material requests.
    for (const r of pendingMaterialReqs.slice(0, 5)) {
      if (out.length >= 5) break;
      const project = projects.find((p) => p.id === r.projectId);
      const itemCount = r.items?.length ?? 0;
      out.push({
        key: `mat-${r.id}`,
        iconClass: 'ph-bold ph-package',
        iconBg: '#EFF6FF',
        iconFg: '#2563EB',
        desc: `Approve material request · ${r.title || `${itemCount} item${itemCount === 1 ? '' : 's'}`}`,
        meta: `${fmtInr(r.totalValue ?? 0)} · ${project?.name ?? 'Project'} · ${relTime(r.createdAt?.toMillis?.() ?? null)}`,
        action: 'Review',
        onClick: () =>
          navigate(`/projects/${r.projectId}?tab=material`),
      });
    }

    return out;
  }, [
    transactions,
    pendingTxnApprovals,
    pendingMaterialReqs,
    projects,
    today,
    navigate,
  ]);

  // ── This-week calendar — 7-day strip ──────────────────────
  const week = useMemo(() => {
    const start = startOfWeekMonday(today);
    const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dayMs = d.getTime();
      const nextMs = dayMs + 86400000;
      const isToday =
        `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` === todayKey;
      // Bucket appointments by day.
      const dots: AppointmentItem[] = [];
      for (const a of appointments) {
        const at = a.scheduledAt?.toMillis?.();
        if (!at) continue;
        if (at >= dayMs && at < nextMs) dots.push(a);
      }
      return { date: d, isToday, appointments: dots };
    });
  }, [appointments, today]);

  // ── Recent activity — pulled from the latest transactions,
  // material requests, and leads. Newest 6 items combined into a
  // single feed. (We don't have a dedicated activity collection
  // yet; this gives a faithful "what's happening" stream from real
  // writes.)
  const activity = useMemo(() => {
    type Row = { ms: number; text: string; sub: string };
    const rows: Row[] = [];
    for (const t of transactions.slice(0, 10)) {
      const ms = t.createdAt?.toMillis?.() ?? txDateMs(t);
      const tp = normaliseTxType(t.type);
      const verb = tp === 'in' ? 'received' : tp === 'out' ? 'paid' : 'logged';
      const project = projects.find((p) => p.id === t.projectId);
      rows.push({
        ms,
        text: `${verb} ${fmtInr(t.amount)}`,
        sub: `${t.partyName ?? 'Transaction'} · ${project?.name ?? 'Project'}`,
      });
    }
    for (const r of materialRequests.slice(0, 5)) {
      const ms = r.createdAt?.toMillis?.() ?? 0;
      const project = projects.find((p) => p.id === r.projectId);
      const itemCount = r.items?.length ?? 0;
      rows.push({
        ms,
        text: `Material request ${r.status === 'pending' ? 'submitted' : r.status}`,
        sub: `${itemCount} item${itemCount === 1 ? '' : 's'} · ${project?.name ?? 'Project'}`,
      });
    }
    for (const l of leads.slice(0, 5)) {
      const ms = l.createdAt?.toMillis?.() ?? 0;
      rows.push({
        ms,
        text: `New lead — ${l.name}`,
        sub: `${l.source ?? 'unknown source'}${l.priority ? ` · ${l.priority} priority` : ''}`,
      });
    }
    return rows
      .filter((r) => r.ms > 0)
      .sort((a, b) => b.ms - a.ms)
      .slice(0, 6);
  }, [transactions, materialRequests, leads, projects]);

  // ── Pipeline value sparkline placeholder data — just a
  // simple count-of-leads-by-week line. Real charting library not
  // yet wired; we hand-roll a tiny SVG to match the design folder.
  const pipelineSpark = useMemo(
    () => buildLeadCountSpark(leads),
    [leads],
  );
  const revenueSpark = useMemo(
    () => buildRevenueSpark(transactions),
    [transactions],
  );

  return (
    <div
      className="page active"
      id="page-dashboard"
      style={{ display: 'block' }}
    >
      {/* 1 — Greeting */}
      <div className="greeting">
        <div className="greeting-left">
          <h1>{greeting}</h1>
          <p>
            {todayLabel}
            {orgData.name ? ` · ${orgData.name}` : ''}
          </p>
        </div>
        {focusBanner ? (
          <div className="greeting-focus">
            <i className="ph-bold ph-warning-circle" />
            {focusBanner}
          </div>
        ) : null}
      </div>

      {/* 2 — KPI strip */}
      <div className="kpi-strip">
        <KpiTile
          label="Active projects"
          value={String(kpiProjects.active)}
          sub={
            <>
              {kpiProjects.total} total ·{' '}
              {kpiProjects.nearing > 0 ? (
                <span className="down">
                  {kpiProjects.nearing} nearing deadline
                </span>
              ) : (
                <span>on schedule</span>
              )}
            </>
          }
          spark={null}
          onClick={() => navigate('/projects')}
        />
        <KpiTile
          label="Revenue this month"
          value={fmtInr(kpiRevenue.thisMonth)}
          sub={
            kpiRevenue.deltaPct != null ? (
              <>
                {kpiRevenue.deltaPct >= 0 ? (
                  <span className="up">+{kpiRevenue.deltaPct}%</span>
                ) : (
                  <span className="down">{kpiRevenue.deltaPct}%</span>
                )}{' '}
                vs last month
              </>
            ) : (
              <>No prior month to compare</>
            )
          }
          spark={revenueSpark}
          onClick={() => navigate('/ledger')}
        />
        <KpiTile
          label="Outstanding receivables"
          value={fmtInr(kpiReceivables.outstanding)}
          sub={
            kpiReceivables.overdueCount > 0 ? (
              <span className="down">
                {kpiReceivables.overdueCount} overdue
              </span>
            ) : kpiReceivables.outstanding > 0 ? (
              <>All within terms</>
            ) : (
              <>Nothing outstanding</>
            )
          }
          spark={null}
          onClick={() => navigate('/ledger')}
        />
        <KpiTile
          label="Pipeline value"
          value={fmtInr(kpiPipeline.value)}
          sub={
            <>
              {kpiPipeline.active} active lead
              {kpiPipeline.active === 1 ? '' : 's'}
            </>
          }
          spark={pipelineSpark}
          onClick={() => navigate('/leads')}
        />
      </div>

      {/* 3 — 60/40 row: attention + (calendar + activity) */}
      <div className="dash-row">
        <div className="card">
          <div className="card-header">
            <div className="card-title">Needs your attention</div>
            <div className="card-count">{attentionItems.length}</div>
          </div>
          {attentionItems.length === 0 ? (
            <div
              style={{
                padding: 36,
                textAlign: 'center',
                color: 'var(--text-tertiary)',
                fontSize: 13,
              }}
            >
              <div
                style={{
                  fontSize: 28,
                  marginBottom: 8,
                  color: 'var(--success)',
                  opacity: 0.6,
                }}
              >
                <i className="ph-fill ph-check-circle" />
              </div>
              You're all caught up. No pending approvals.
            </div>
          ) : (
            <ul className="attn-list" style={{ margin: 0, padding: 0 }}>
              {attentionItems.map((it) => (
                <li
                  key={it.key}
                  className="attn-item"
                  onClick={it.onClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      it.onClick();
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="attn-icon"
                    style={{ background: it.iconBg, color: it.iconFg }}
                  >
                    <i className={it.iconClass} />
                  </div>
                  <div className="attn-body">
                    <div className="attn-desc">{it.desc}</div>
                    <div className="attn-meta">{it.meta}</div>
                  </div>
                  <div className="attn-action">
                    <button
                      type="button"
                      className={`btn-action${it.critical ? ' critical' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        it.onClick();
                      }}
                    >
                      {it.action}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <div className="card-title">This week</div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  fontFamily: 'Menlo, monospace',
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                }}
              >
                {week[0].date.toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                })}{' '}
                –{' '}
                {week[6].date.toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'short',
                })}
              </div>
            </div>
            <div className="cal-strip">
              {week.map((d) => (
                <button
                  key={d.date.toISOString()}
                  type="button"
                  className={`cal-day${d.isToday ? ' today' : ''}`}
                  onClick={() => navigate('/calendar')}
                  style={{
                    border: 'none',
                    background: d.isToday ? '#FFFBEB' : 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                  title={
                    d.appointments.length > 0
                      ? `${d.appointments.length} appointment${d.appointments.length === 1 ? '' : 's'}`
                      : 'No appointments'
                  }
                >
                  <div className="cal-day-label">
                    {d.date.toLocaleDateString('en-IN', {
                      weekday: 'short',
                    })}
                  </div>
                  <div className="cal-day-num">{d.date.getDate()}</div>
                  <div className="cal-dots">
                    {d.appointments.slice(0, 4).map((a) => (
                      <span
                        key={a.id}
                        className={`cal-dot ${apptTypeClass(a.type)}`}
                      />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Recent activity</div>
            </div>
            {activity.length === 0 ? (
              <div
                style={{
                  padding: 24,
                  fontSize: 12,
                  color: 'var(--text-tertiary)',
                  textAlign: 'center',
                }}
              >
                Activity stream will populate as the team posts.
              </div>
            ) : (
              <ul className="activity-list" style={{ margin: 0, padding: 0 }}>
                {activity.map((row, i) => (
                  <li key={i} className="activity-item">
                    <strong>{row.text}</strong>
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        marginTop: 2,
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                      }}
                    >
                      <span>{row.sub}</span>
                      <span>·</span>
                      <span className="activity-time">
                        {relTime(row.ms)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* 4 — 50/50 row: today's tasks + staff list */}
      <div
        className="dash-row"
        style={{ gridTemplateColumns: '1fr 1fr' }}
      >
        <div className="card">
          <div className="card-header">
            <div className="card-title">Active projects</div>
            <div className="card-count">{kpiProjects.active}</div>
          </div>
          {projects.filter((p) => p.status === 'active').length === 0 ? (
            <div
              style={{
                padding: 32,
                fontSize: 12,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              No active projects yet — create one from Projects.
            </div>
          ) : (
            <ul
              className="activity-list"
              style={{ margin: 0, padding: 0 }}
            >
              {projects
                .filter((p) => p.status === 'active')
                .slice(0, 5)
                .map((p) => (
                  <li
                    key={p.id}
                    className="activity-item"
                    onClick={() => navigate(`/projects/${p.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                          }}
                        >
                          {p.name}
                        </strong>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text-tertiary)',
                            marginTop: 2,
                          }}
                        >
                          {p.location ?? p.siteAddress ?? '—'}
                        </div>
                      </div>
                      <div
                        style={{
                          width: 80,
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            height: 4,
                            background: '#F1F5F9',
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.max(0, Math.min(100, p.progress ?? 0))}%`,
                              height: '100%',
                              background: 'var(--brand)',
                              transition: 'width 0.3s',
                            }}
                          />
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            textAlign: 'right',
                            marginTop: 2,
                            fontFamily: 'Menlo, monospace',
                          }}
                        >
                          {p.progress ?? 0}%
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">Studio team</div>
            <div className="card-count">{members.length}</div>
          </div>
          {members.length === 0 ? (
            <div
              style={{
                padding: 32,
                fontSize: 12,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              No team members yet.
            </div>
          ) : (
            <ul
              className="activity-list"
              style={{ margin: 0, padding: 0 }}
            >
              {members.slice(0, 5).map((m) => {
                const tone = ROLE_TONE[m.role];
                return (
                  <li
                    key={m.uid}
                    className="activity-item"
                    onClick={() => navigate('/team')}
                    style={{ cursor: 'pointer' }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: m.photoURL
                            ? `center / cover no-repeat url(${m.photoURL})`
                            : tone.bg,
                          color: tone.fg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {m.photoURL ? '' : memberInitials(m.displayName)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block',
                          }}
                        >
                          {m.displayName}
                          {m.isOwner ? (
                            <i
                              className="ph-fill ph-crown"
                              style={{
                                color: '#D97706',
                                marginLeft: 6,
                                fontSize: 11,
                              }}
                            />
                          ) : null}
                        </strong>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            marginTop: 2,
                          }}
                        >
                          {ROLE_LABELS[m.role]}
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: 999,
                          background: tone.bg,
                          color: tone.fg,
                        }}
                      >
                        {ROLE_LABELS[m.role].toUpperCase()}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* 5 — 50/50 row: pending material + pending transactions */}
      <div
        className="dash-row"
        style={{ gridTemplateColumns: '1fr 1fr' }}
      >
        <div className="card">
          <div className="card-header">
            <div className="card-title">Pending material requests</div>
            <div className="card-count">{pendingMaterialReqs.length}</div>
          </div>
          {pendingMaterialReqs.length === 0 ? (
            <div
              style={{
                padding: 32,
                fontSize: 12,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              No pending material requests.
            </div>
          ) : (
            <ul className="attn-list" style={{ margin: 0, padding: 0 }}>
              {pendingMaterialReqs.slice(0, 5).map((r) => (
                <MaterialReqRow
                  key={r.id}
                  r={r}
                  projectName={
                    projects.find((p) => p.id === r.projectId)?.name
                  }
                  onClick={() =>
                    navigate(`/projects/${r.projectId}?tab=material`)
                  }
                />
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">Pending transaction approvals</div>
            <div className="card-count">{pendingTxnApprovals.length}</div>
          </div>
          {pendingTxnApprovals.length === 0 ? (
            <div
              style={{
                padding: 32,
                fontSize: 12,
                color: 'var(--text-tertiary)',
                textAlign: 'center',
              }}
            >
              No pending transaction approvals.
            </div>
          ) : (
            <ul className="attn-list" style={{ margin: 0, padding: 0 }}>
              {pendingTxnApprovals.slice(0, 5).map((t) => (
                <TxnApprovalRow
                  key={t.id}
                  t={t}
                  projectName={
                    projects.find((p) => p.id === t.projectId)?.name
                  }
                  onClick={() =>
                    navigate(
                      `/projects/${t.projectId}?tab=transactions&txn=${t.id}`,
                    )
                  }
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ───── Row components ─────

function MaterialReqRow({
  r,
  projectName,
  onClick,
}: {
  r: MaterialRequest;
  projectName: string | undefined;
  onClick: () => void;
}) {
  const itemCount = r.items?.length ?? 0;
  return (
    <li
      className="attn-item"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div
        className="attn-icon"
        style={{ background: '#EFF6FF', color: '#2563EB' }}
      >
        <i className="ph-bold ph-package" />
      </div>
      <div className="attn-body">
        <div className="attn-desc">
          {r.title || `Request — ${itemCount} item${itemCount === 1 ? '' : 's'}`}
        </div>
        <div className="attn-meta">
          {fmtInr(r.totalValue ?? 0)} ·{' '}
          {projectName ?? 'Project'} ·{' '}
          {relTime(r.createdAt?.toMillis?.() ?? null)}
        </div>
      </div>
      <div className="attn-action">
        <button
          type="button"
          className="btn-action"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          Review
        </button>
      </div>
    </li>
  );
}

function TxnApprovalRow({
  t,
  projectName,
  onClick,
}: {
  t: TransactionItem;
  projectName: string | undefined;
  onClick: () => void;
}) {
  const tp = normaliseTxType(t.type);
  const iconBg = tp === 'in' ? '#F0FDF4' : tp === 'out' ? 'var(--critical-bg)' : '#F1F5F9';
  const iconFg = tp === 'in' ? '#16A34A' : tp === 'out' ? 'var(--critical)' : '#475569';
  return (
    <li
      className="attn-item"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div className="attn-icon" style={{ background: iconBg, color: iconFg }}>
        <i
          className={
            tp === 'in'
              ? 'ph-fill ph-arrow-down-right'
              : tp === 'out'
                ? 'ph-fill ph-arrow-up-right'
                : 'ph ph-receipt'
          }
        />
      </div>
      <div className="attn-body">
        <div className="attn-desc">
          {t.description || t.partyName || 'Transaction'}
        </div>
        <div className="attn-meta">
          {fmtInr(t.amount)} ·{' '}
          {projectName ?? 'Project'} ·{' '}
          {relTime(t.createdAt?.toMillis?.() ?? null)}
        </div>
      </div>
      <div className="attn-action">
        <button
          type="button"
          className={tp === 'out' ? 'btn-action critical' : 'btn-action'}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          Approve
        </button>
      </div>
    </li>
  );
}

// ───── KPI tile + sparklines ─────

function KpiTile({
  label,
  value,
  sub,
  spark,
  onClick,
}: {
  label: string;
  value: string;
  sub: React.ReactNode;
  spark: React.ReactNode | null;
  onClick?: () => void;
}) {
  return (
    <div
      className="kpi-tile"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s, box-shadow 0.1s',
      }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="kpi-label">{label}</div>
      <div className="kpi-value tn">{value}</div>
      <div className="kpi-sub">{sub}</div>
      {spark ? <div className="kpi-sparkline">{spark}</div> : null}
    </div>
  );
}

/** Tiny SVG line sparkline — 6 weekly buckets of payment_in totals.
 *  Hand-rolled so we don't need to install a chart library. */
function buildRevenueSpark(transactions: TransactionItem[]): React.ReactNode {
  const now = new Date();
  const buckets: number[] = [0, 0, 0, 0, 0, 0];
  for (const t of transactions) {
    if (normaliseTxType(t.type) !== 'in') continue;
    const ms = txDateMs(t);
    const ageWeeks = Math.floor((now.getTime() - ms) / (7 * 86400000));
    if (ageWeeks < 0 || ageWeeks >= 6) continue;
    buckets[5 - ageWeeks] += t.amount;
  }
  return renderSpark(buckets);
}

function buildLeadCountSpark(leads: LeadListItem[]): React.ReactNode {
  const now = new Date();
  const buckets: number[] = [0, 0, 0, 0, 0, 0];
  for (const l of leads) {
    const ms = l.createdAt?.toMillis?.() ?? 0;
    if (!ms) continue;
    const ageWeeks = Math.floor((now.getTime() - ms) / (7 * 86400000));
    if (ageWeeks < 0 || ageWeeks >= 6) continue;
    buckets[5 - ageWeeks] += 1;
  }
  return renderSpark(buckets);
}

function renderSpark(buckets: number[]): React.ReactNode {
  // Normalise to 0..1.
  const max = Math.max(...buckets, 1);
  const pts = buckets.map((b, i) => {
    const x = (i / (buckets.length - 1)) * 100;
    const y = 28 - (b / max) * 24 - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  if (max === 0) return null;
  return (
    <svg viewBox="0 0 100 28" preserveAspectRatio="none">
      <polyline className="line" points={pts.join(' ')} />
      <circle
        className="dot"
        cx={pts[pts.length - 1].split(',')[0]}
        cy={pts[pts.length - 1].split(',')[1]}
        r="2"
      />
    </svg>
  );
}

// ───── Misc helpers ─────

/** Best-guess monetary value for a lead — prefers the typed `budget`
 *  number, falls back to the midpoint of the chosen `budgetRange`
 *  bucket. */
function budgetEstimate(l: LeadListItem): number {
  if (typeof l.budget === 'number' && l.budget > 0) return l.budget;
  switch (l.budgetRange) {
    case 'under_10l': return 7_50_000;
    case '10_15l':   return 12_50_000;
    case '15_25l':   return 20_00_000;
    case '25_40l':   return 32_50_000;
    case 'above_40l':return 60_00_000;
    default: return 0;
  }
}

function apptTypeClass(t: AppointmentItem['type']): string {
  switch (t) {
    case 'site_visit':    return 'visit';
    case 'office_meeting':return 'meeting';
    case 'virtual_call':  return 'meeting';
    default:              return 'visit';
  }
}
